import { Toast, ToastPackage, ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { animate, keyframes, state, style, transition, trigger } from '@angular/animations';
import { Component, ElementRef, HostBinding, HostListener, OnDestroy, OnInit } from '@angular/core';

import { MxEventName, MxPropertyName } from '@axon-enterprise/track';
import { SnAlarm, SnCamera } from '@lib/api/entities';
import { SnCurrentOrganization, SnCurrentUser } from '@lib/auth';
import { ALERT_NOTIFICATION_VERSION } from '@lib/axon/services/alerts.constants';
import { hasCoordinates } from '@lib/geo/helpers';
import { MixpanelService } from '@lib/mixpanel';
import { SnDialogService } from '@lib/ui/dialog';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[fs-alarm-toast]',
  templateUrl: './alarm-toast.component.html',
  styleUrls: ['./alarm-toast.component.scss'],
  preserveWhitespaces: false,
  animations: [
    trigger('flyInOut', [
      state(
        'inactive',
        style({
          opacity: 0,
        }),
      ),
      transition(
        'inactive => active',
        animate(
          '400ms ease-out',
          keyframes([
            style({
              transform: 'translate3d(100%, 0, 0) skewX(-30deg)',
              opacity: 0,
            }),
            style({
              transform: 'skewX(20deg)',
              opacity: 1,
            }),
            style({
              transform: 'skewX(-5deg)',
              opacity: 1,
            }),
            style({
              transform: 'none',
              opacity: 1,
            }),
          ]),
        ),
      ),
      transition(
        'active => removed',
        animate(
          '400ms ease-out',
          keyframes([
            style({
              opacity: 1,
            }),
            style({
              transform: 'translate3d(100%, 0, 0) skewX(30deg)',
              opacity: 0,
            }),
          ]),
        ),
      ),
    ]),
  ],
})
export class AlarmToastComponent extends Toast implements OnDestroy, OnInit {
  private _alarm: SnAlarm;

  @HostBinding('style.border')
  public border = 'none';

  @HostBinding('style.box-shadow')
  public boxShadow = '0 0 12px #999999;';

  public count: number = 0;

  public autoDockMaxCameras: number;

  public hasCoordinates: boolean = false;

  private wasInside = false;

  private readonly _destroy$ = new Subject<void>();

  private readonly _view$ = new Subject<SnAlarm>();
  private readonly _dockCameras$ = new Subject<SnAlarm>();
  private readonly _showDialog$ = new Subject<SnAlarm>();
  private readonly _mutedReasons$ = new Subject<SnAlarm>();

  cxVersion?: string;
  constructor(
    protected toastrService: ToastrService,
    public toastPackage: ToastPackage,
    public element: ElementRef,
    private readonly _dialogService: SnDialogService,
    private readonly _mixpanelService: MixpanelService,
    private readonly _snCurrentUser: SnCurrentUser,
    private readonly _currentOrganization: SnCurrentOrganization,
  ) {
    super(toastrService, toastPackage);
    this.autoDockMaxCameras = this._currentOrganization.settings.autoDockMaxCameras;
    this.cxVersion = ALERT_NOTIFICATION_VERSION.LEGACY_CX;
  }

  get view$(): Observable<SnAlarm> {
    return this._view$.asObservable();
  }

  get dockCameras$(): Observable<SnAlarm> {
    return this._dockCameras$.asObservable();
  }

  get showDialog$(): Observable<SnAlarm> {
    return this._showDialog$.asObservable();
  }

  get mutedReasons$(): Observable<SnAlarm> {
    return this._mutedReasons$.asObservable();
  }

  public get alarm(): SnAlarm {
    return this._alarm;
  }

  public set alarm(value: SnAlarm) {
    this._alarm = value;
    this.hasCoordinates = hasCoordinates(value?.geometry?.coordinates);
  }

  @HostListener('click', ['$event'])
  public onClick(event: Event): void {
    if (this.element.nativeElement.contains(event.target)) {
      this.border = '3px solid rgba(81, 203, 238, 1)';
      this.boxShadow = '0 0 5px rgba(81, 203, 238, 1)';
      this.wasInside = true;
      this.hasCoordinates
        ? this._dockCameras$.next(this.alarm)
        : this._showDialog$.next(this.alarm);
    }
  }

  @HostListener('document:click')
  public clickOut(): void {
    if (!this.wasInside) {
      this.border = 'none';
      this.boxShadow = '0 0 12px #999999;';
    }
    this.wasInside = false;
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  public viewAlert(): void {
    this._view$.next(this.alarm);
    
    const camera = this.alarm.source instanceof SnCamera ? this.alarm.source : null;
    
    this._mixpanelService.trackMetric(MxEventName.ViewMarkerMapAf, {
      [MxPropertyName.FususMarkerType]: 'Alert',
      [MxPropertyName.FususCamOwnerID]: camera?.organization?.id || 'N/A',
      [MxPropertyName.FususViewerOrganizationID]: this._currentOrganization.id,
      [MxPropertyName.FususCamOrgOwner]: camera?.organization?.name || 'N/A',
      [MxPropertyName.FususCamOrgViewer]: this._currentOrganization.name,
      [MxPropertyName.FususDeviceCategory]: '',
      [MxPropertyName.FususDeviceBrand]: camera?.make || 'N/A',
      [MxPropertyName.DeviceModel]: camera?.model || 'N/A',
      [MxPropertyName.EventReportType]: this.alarm.type.toUpperCase().includes('LPR')
      ? 'ALPR'
      : this.alarm.type,
      [MxPropertyName.FususIntegration]: this.alarm?.type ?? '',
    });
  }



  public dockCameras(): void {
    this._dockCameras$.next(this.alarm);
  }

  public muteAlarmReasons(): void {
    const alarmReasonMessage = this.alarm.message;
    this._dialogService
      .confirm({
        message: `
        This will turn off your ${alarmReasonMessage} notifications.
        To re-enable them go to the layers menu. Are you sure?`,
        confirmButton: {
          text: 'Confirm',
          color: 'accent',
        },
      })
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this.toastrService.success(
            `This ALPR Notification ${this.alarm.message} has been turned off`,
          );
          this._mutedReasons$.next(this.alarm);
        },
        error: () => {
          this.toastrService.error('Unable to turn this ALPR Notification off');
        },
      });
  }

  public customToolTip(): string {
    return `Turn off ${this.alarm?.message} ALPR Notifications`;
  }

  public trackNotificationDismiss(): void {
    this._mixpanelService.trackMetric(MxEventName.NotificationDismissedNtfAf, {
      [MxPropertyName.FususDeviceCategory]: '',
      [MxPropertyName.FususDeviceBrand]:
        this.alarm.source instanceof SnCamera ? this.alarm.source.make : '',
      [MxPropertyName.DeviceModel]:
        this.alarm.source instanceof SnCamera ? this.alarm.source.model : '',
      [MxPropertyName.DeviceID]: this.alarm.source instanceof SnCamera ? this.alarm.source.id : '',
      [MxPropertyName.EventReportType]: this.alarm.type.toUpperCase().includes('LPR')
      ? 'ALPR'
      : this.alarm.type,
      [MxPropertyName.FususIntegration]: this.alarm?.type ?? '',
      [MxPropertyName.UserID]: this._snCurrentUser.id,
      [MxPropertyName.NotificationID]: this.alarm.id,
      [MxPropertyName.FususDeliveryChannel]: 'In-App',
      [MxPropertyName.UserDevice]: 'Web',
      [MxPropertyName.CxVersion]: ALERT_NOTIFICATION_VERSION.LEGACY_CX,
    });
  }
}
